import { Component, OnInit, OnDestroy } from '@angular/core';
import { UIFilterBase } from '../UIFilterBase';
import { DateUIQuery, DatesFilterItemBase, LastDaysFilterItem, FixedDatesFilterItem } from './filter-custom-date.model';
import { FilterService } from '../../../services/filter.service';
import { TranslateService } from '@ngx-translate/core';
import { FilterElement } from '../FilterElement';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { Platform } from '@angular/cdk/platform';
import dayjs from 'dayjs';
import { DAYJS_DATE_FORMATS, DayjsDateAdapter } from 'src/app/core/utils/dayjs-date-adapter';

@Component({
  selector: 'core-filter-custom-date',
  templateUrl: './filter-custom-date.component.html',
  styleUrls: ['./filter-custom-date.component.scss'],
  providers: [
    { provide: DateAdapter, useClass: DayjsDateAdapter, deps: [MAT_DATE_LOCALE, Platform] },
    { provide: MAT_DATE_FORMATS, useValue: DAYJS_DATE_FORMATS }
  ],
  standalone: false
})
export class FilterCustomDateComponent extends UIFilterBase<DateUIQuery> implements OnInit, OnDestroy {
  private static FilterCustomDateKey = 'FilterCustomDate';

  lastDays: FilterElement<LastDaysFilterItem>;
  fixedDates: FilterElement<FixedDatesFilterItem>;

  public unitsFormGroup: UntypedFormGroup;
  public datesFormGroup: UntypedFormGroup;

  constructor(filterService: FilterService, translateService: TranslateService) {
    super(filterService, translateService, FilterCustomDateComponent.FilterCustomDateKey);
  }

  ngOnInit(): void {
    this.intializeValidators();
    this.initialize();
  }

  public hasErrorUnits = (errorName: string): boolean => {
    return this.unitsFormGroup.controls.units.hasError(errorName);
  };

  public hasErrorDates = (controlName: string, errorName: string): boolean => {
    return this.datesFormGroup.controls[controlName].hasError(errorName);
  };

  private intializeValidators() {
    /* eslint-disable @typescript-eslint/unbound-method */
    this.unitsFormGroup = new UntypedFormGroup({
      units: new UntypedFormControl('', [Validators.required, Validators.min(1), Validators.pattern('^[0-9]*$')])
    });

    this.datesFormGroup = new UntypedFormGroup({
      dateFrom: new UntypedFormControl('', [Validators.required, this.validateDates.bind(this)]),
      dateTo: new UntypedFormControl('', [Validators.required, this.validateDates.bind(this)]),
    });
    /* eslint-enable @typescript-eslint/unbound-method */
  }

  private validateDates(c: UntypedFormControl) {

    let isValid = true;
    if (this && this.datesFormGroup) {
      const dateFrom = this.datesFormGroup.controls.dateFrom;
      const dateTo = this.datesFormGroup.controls.dateTo;
      if (dateFrom && dateTo && dateFrom.value && dateTo.value) {
        const dateFromValue: Date = dateFrom.value as Date;
        const dateToValue: Date = dateTo.value as Date;
        if (!(dayjs(dateFromValue).isBefore(dayjs(dateToValue)))) {
          isValid = false;
        }
      }
    }

    if (isValid) {
      return true;
    } else {
      return {
        validateDates: {
          valid: isValid
        }
      };
    }
  }


  private initialize() {

    const query = this.filterService.getDateUIQuery();
    this.initializeQuery(query);

    this.initializeDates();
  }

  private initializeDates() {
    this.lastDays = this.query.getLastDays();
    this.fixedDates = this.query.getFixedDates();

    this.datesFormGroup.setValue(
      {
        dateFrom: this.fixedDates.item.dateFrom.toDate(),
        dateTo: this.fixedDates.item.dateTo.toDate()
      }
    );

    this.unitsFormGroup.setValue({
      units: this.lastDays.item.units
    });
  }

  protected queryReset(): void {
    this.initializeDates();
  }

  selectItem(date: FilterElement<DatesFilterItemBase>): void {
    const previousSelection = this.query.getSelected();
    if (previousSelection) {
      if (previousSelection.item.getId() !== date.item.getId()) {
        previousSelection.toggleSelected();
        date.toggleSelected();
        this.filterService.updateDateQuery(this.query);
      }
    }
  }

  startDateChanged(date: MatDatepickerInputEvent<dayjs.Dayjs>): void {
    this.fixedDates.item.dateFrom = dayjs(date.value);
    this.filterService.updateDateQuery(this.query);

    this.runDateValidation();
  }

  endDateChanged(date: MatDatepickerInputEvent<dayjs.Dayjs>): void {
    this.fixedDates.item.dateTo = dayjs(date.value);
    this.filterService.updateDateQuery(this.query);

    this.runDateValidation();
  }

  runDateValidation(): void {
    this.datesFormGroup.get('dateFrom').updateValueAndValidity();
    this.datesFormGroup.get('dateTo').updateValueAndValidity();
  }

  fixedDaysChanged(event: Event): void {
    if (this.hasErrorUnits('pattern')) {
      return;
    }
    const target = event.target as HTMLInputElement;
    this.lastDays.item.units = +target.value;
    this.filterService.updateDateQuery(this.query);
  }

  timeRangeChanged(): void {
    this.filterService.updateDateQuery(this.query);
  }

  ngOnDestroyInternal(): void {
    // Required by base component
  }

  clearFilter(): void {
    this.query.reset();
    this.filterService.updateDateQuery(this.query);
  }
}
